<template>
  <!-- Backdrop -->
  <Transition name="fade">
    <div
      v-if="open"
      class="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
      @click="$emit('close')"
    />
  </Transition>

  <!-- Drawer panel -->
  <Transition name="slide">
    <aside
      v-if="open"
      class="fixed inset-y-0 right-0 z-50 w-full sm:w-[480px] flex flex-col bg-charcoal-800/90 backdrop-blur-2xl border-l border-white/20 shadow-2xl"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-5 border-b border-charcoal-700"
        :class="headerBg"
      >
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl flex items-center justify-center" :class="iconBg">
            <component :is="statusIcon" :size="20" :stroke-width="2" :class="iconColor" />
          </div>
          <div>
            <h3 class="text-base font-black tracking-tight" :class="titleColor">
              {{ statusLabel }}
            </h3>
            <p class="text-xs text-charcoal-400">{{ props.count }} {{ displayUnit }}</p>
          </div>
        </div>
        <button
          class="w-9 h-9 rounded-xl flex items-center justify-center text-charcoal-400 hover:text-charcoal-300 hover:bg-charcoal-700 transition-colors"
          @click="$emit('close')"
        >
          <X :size="18" :stroke-width="2.5" />
        </button>
      </div>

      <!-- Status description -->
      <div class="px-6 py-3 border-b border-charcoal-700 bg-charcoal-900/50">
        <p class="text-xs text-charcoal-400">
          {{ statusDescription }}
        </p>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        <!-- Loading -->
        <div v-if="loading" class="space-y-3">
          <div v-for="i in 3" :key="i" class="h-24 rounded-2xl bg-charcoal-700 animate-pulse" />
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="flex flex-col items-center justify-center py-16 text-center">
          <div class="w-14 h-14 rounded-2xl bg-charcoal-700 flex items-center justify-center mb-4">
            <AlertCircle :size="28" class="text-rose-400" :stroke-width="1.5" />
          </div>
          <p class="text-sm font-semibold text-rose-500">
            {{ error }}
          </p>
          <p class="text-xs text-charcoal-400 mt-1">Please close and try again.</p>
        </div>

        <!-- Not started: surveys with no report yet -->
        <template v-else-if="props.status === 'not_started'">
          <div
            v-if="props.notStartedSurveys.length === 0 && !loading"
            class="flex flex-col items-center justify-center py-16 text-center"
          >
            <div
              class="w-14 h-14 rounded-2xl bg-charcoal-700 flex items-center justify-center mb-4"
            >
              <CheckCircle :size="28" class="text-charcoal-400" :stroke-width="1.5" />
            </div>
            <p class="text-sm font-semibold text-charcoal-400">All surveys have a report.</p>
            <p class="text-xs text-charcoal-400 mt-1">No surveys without a report were found.</p>
          </div>
          <div
            v-for="survey in props.notStartedSurveys"
            :key="survey.surveyId"
            class="rounded-2xl border bg-charcoal-800 border-slate-100 p-4"
          >
            <p class="font-bold text-sm text-charcoal-100 truncate leading-tight mb-1">
              {{ survey.surveyName }}
            </p>
            <p class="text-xs text-charcoal-400 font-mono truncate">
              {{ survey.surveyId }}
            </p>
            <p class="mt-2 text-[10px] text-slate-400">
              Open in Sites &amp; Surveys to start a report
            </p>
          </div>
        </template>

        <!-- Started by vendor: report cards -->
        <template v-else-if="props.status === 'started_by_vendor'">
          <div
            v-if="reports.length === 0 && !loading"
            class="flex flex-col items-center justify-center py-16 text-center"
          >
            <div
              class="w-14 h-14 rounded-2xl bg-charcoal-700 flex items-center justify-center mb-4"
            >
              <CheckCircle :size="28" class="text-charcoal-400" :stroke-width="1.5" />
            </div>
            <p class="text-sm font-semibold text-charcoal-400">No reports here</p>
          </div>
          <button
            v-for="report in reports"
            :key="report.id"
            type="button"
            class="w-full text-left rounded-2xl border bg-charcoal-800 border-indigo-100 p-4 hover:shadow-md hover:border-indigo-300 hover:bg-indigo-50/30 transition-all cursor-pointer group"
            @click="navigateToReport(report)"
          >
            <div class="flex items-start justify-between gap-2 mb-1">
              <p class="font-bold text-sm text-charcoal-100 truncate leading-tight">
                {{ report.surveyName }}
              </p>
              <ArrowRight
                :size="14"
                class="shrink-0 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5"
                :stroke-width="2.5"
              />
            </div>
            <div class="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs mt-2">
              <div>
                <span class="text-charcoal-400 block">Last updated</span>
                <span class="font-medium text-charcoal-200">{{
                  formatDate(report.updatedAt)
                }}</span>
              </div>
              <div>
                <span class="text-charcoal-400 block">Created</span>
                <span class="font-medium text-charcoal-200">{{
                  formatDate(report.createdAt)
                }}</span>
              </div>
            </div>
            <p class="mt-2 text-[10px] text-indigo-400 flex items-center gap-1">
              <ExternalLink :size="9" :stroke-width="2.5" />
              Click to open proposal
            </p>
          </button>
        </template>

        <!-- Empty proposals -->
        <div
          v-else-if="proposals.length === 0 && !loading && props.status !== 'stale_reports'"
          class="flex flex-col items-center justify-center py-16 text-center"
        >
          <div class="w-14 h-14 rounded-2xl bg-charcoal-700 flex items-center justify-center mb-4">
            <CheckCircle :size="28" class="text-charcoal-400" :stroke-width="1.5" />
          </div>
          <p class="text-sm font-semibold text-charcoal-400">No proposals here</p>
        </div>

        <!-- Stale reports: list of affected surveys with delete/relink/adhoc actions -->
        <template v-else-if="props.status === 'stale_reports'">
          <p class="text-xs text-charcoal-400 mb-3">
            These surveys no longer exist in Site Compass but still have reports saved locally. You
            can re-link them to an existing survey, convert them to ad-hoc reports, or delete them.
          </p>

          <!-- Inline confirmation banner -->
          <div
            v-if="pendingDelete !== null"
            class="mb-3 rounded-xl border border-charcoal-600 bg-charcoal-700 p-4"
          >
            <p class="text-sm font-semibold text-red-400 mb-1">Are you sure?</p>
            <p class="text-xs text-charcoal-300 mb-3">
              {{
                pendingDelete.type === 'all'
                  ? `This will permanently delete all ${props.staleSurveys.length} stale survey reports. This cannot be undone.`
                  : `This will permanently delete ${pendingDelete.reportCount} report(s) for "${pendingDelete.reportName}". This cannot be undone.`
              }}
            </p>
            <div class="flex gap-2">
              <button
                type="button"
                class="flex-1 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition-colors"
                @click="confirmDelete"
              >
                Yes, delete
              </button>
              <button
                type="button"
                class="flex-1 py-1.5 rounded-lg border border-charcoal-600 text-charcoal-300 text-xs font-semibold hover:bg-charcoal-900 transition-colors"
                @click="pendingDelete = null"
              >
                Cancel
              </button>
            </div>
          </div>

          <div
            v-if="props.staleSurveys.length === 0"
            class="flex flex-col items-center justify-center py-12 text-center"
          >
            <AlertTriangle :size="28" class="text-amber-400 mb-3" :stroke-width="1.5" />
            <p class="text-sm text-charcoal-400">
              {{ props.count }} stale {{ props.count === 1 ? 'report' : 'reports' }} detected
            </p>
            <p class="text-xs text-charcoal-400 mt-1">Survey details unavailable.</p>
          </div>
          <div v-else class="space-y-3">
            <!-- Delete All action -->
            <div class="flex justify-end">
              <button
                type="button"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-charcoal-600 text-red-400 text-xs font-semibold hover:bg-charcoal-600 transition-colors"
                @click="pendingDelete = { type: 'all' }"
              >
                <Trash2 :size="12" :stroke-width="2.5" />
                Delete All ({{ props.staleSurveys.length }})
              </button>
            </div>
            <div
              v-for="survey in props.staleSurveys"
              :key="survey.surveyId"
              class="rounded-2xl border border-charcoal-600 bg-charcoal-700 p-4"
            >
              <!-- Header row: name + status badge + delete -->
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2 flex-wrap mb-0.5">
                    <p class="font-bold text-sm text-charcoal-100 truncate leading-tight">
                      {{ survey.reportName ?? 'Untitled Report' }}
                    </p>
                    <!-- Orphaned badge: a survey with matching name was found -->
                    <span
                      v-if="suggestedSurvey(survey.reportName)"
                      class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-sky-500/20 border border-sky-500/30 text-sky-300 text-[10px] font-bold uppercase tracking-wide shrink-0"
                    >
                      <Link2 :size="9" :stroke-width="2.5" />
                      Orphaned
                    </span>
                    <!-- Foreign badge: no matching survey found -->
                    <span
                      v-else
                      class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 text-[10px] font-bold uppercase tracking-wide shrink-0"
                    >
                      <AlertTriangle :size="9" :stroke-width="2.5" />
                      Foreign
                    </span>
                  </div>
                  <p class="text-xs text-charcoal-400 font-mono truncate">
                    Survey ID: {{ survey.surveyId }}
                  </p>
                  <!-- Orphaned: show which survey it may belong to -->
                  <p v-if="suggestedSurvey(survey.reportName)" class="text-xs text-sky-400 mt-0.5">
                    Possible match:
                    <span class="font-semibold">{{
                      suggestedSurvey(survey.reportName)!.name
                    }}</span>
                  </p>
                  <!-- Foreign: survey is unrecognised -->
                  <p v-else class="text-xs text-red-400 mt-0.5">
                    Original survey not found — re-link manually or convert to ad-hoc.
                  </p>
                </div>
                <div class="shrink-0 flex items-center gap-2">
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-semibold"
                  >
                    {{ survey.reportCount }} {{ survey.reportCount === 1 ? 'report' : 'reports' }}
                  </span>
                  <button
                    type="button"
                    class="w-7 h-7 rounded-lg flex items-center justify-center text-charcoal-400 hover:text-red-400 hover:bg-charcoal-600 transition-colors"
                    :aria-label="`Delete stale reports for survey ${survey.reportName ?? survey.surveyId}`"
                    title="Delete reports for this survey"
                    @click="
                      pendingDelete = {
                        type: 'survey',
                        surveyId: survey.surveyId,
                        reportCount: survey.reportCount,
                        reportName: survey.reportName ?? 'Untitled Report',
                      }
                    "
                  >
                    <Trash2 :size="14" :stroke-width="2" />
                  </button>
                </div>
              </div>

              <!-- Action buttons row -->
              <div class="flex gap-2 mt-3 flex-wrap">
                <!-- Open report button -->
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-charcoal-600 text-charcoal-300 text-xs font-semibold hover:bg-charcoal-600 transition-colors"
                  :title="`Open report for survey ${survey.reportName ?? survey.surveyId}`"
                  @click="navigateToStaleReport(survey.surveyId)"
                >
                  <ArrowRight :size="11" :stroke-width="2.5" />
                  Open report
                </button>
                <!-- Re-link button / toggle -->
                <button
                  v-if="effectiveValidSurveys.length > 0"
                  type="button"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors"
                  :class="
                    relinkOpen[survey.surveyId]
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'border-charcoal-600 text-charcoal-300 hover:bg-charcoal-600'
                  "
                  @click="toggleRelink(survey.surveyId, survey.reportName)"
                >
                  <Link2 :size="11" :stroke-width="2.5" />
                  Re-link
                  <ChevronDown
                    :size="10"
                    :stroke-width="2.5"
                    :class="relinkOpen[survey.surveyId] ? 'rotate-180' : ''"
                    class="transition-transform"
                  />
                </button>
                <!-- Convert to Ad-hoc button -->
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-charcoal-600 text-charcoal-300 text-xs font-semibold hover:bg-charcoal-600 transition-colors"
                  :title="`Convert these reports to ad-hoc (no survey)`"
                  @click="doConvertAdhoc(survey.surveyId)"
                >
                  <LayoutTemplate :size="11" :stroke-width="2.5" />
                  Convert to Ad-hoc
                </button>
              </div>

              <!-- Re-link panel (survey selector) -->
              <div
                v-if="relinkOpen[survey.surveyId] && effectiveValidSurveys.length > 0"
                class="mt-3 p-3 rounded-xl bg-charcoal-800 border border-charcoal-600"
              >
                <p class="text-xs font-semibold text-charcoal-300 mb-2">
                  Select the survey to re-link this report to:
                </p>
                <select
                  :value="relinkSelected[survey.surveyId] ?? effectiveValidSurveys[0]?.id"
                  class="w-full text-xs rounded-lg border border-charcoal-600 bg-charcoal-800 text-charcoal-100 px-2.5 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  @change="
                    e =>
                      (relinkSelected = {
                        ...relinkSelected,
                        [survey.surveyId]: (e.target as HTMLSelectElement).value,
                      })
                  "
                >
                  <option
                    v-for="vs in effectiveValidSurveys"
                    :key="vs.id"
                    :value="vs.id"
                    :selected="
                      (relinkSelected[survey.surveyId] ?? effectiveValidSurveys[0]?.id) === vs.id
                    "
                  >
                    {{ vs.name }}
                  </option>
                </select>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="flex-1 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors"
                    @click="doRelink(survey.surveyId)"
                  >
                    Confirm Re-link
                  </button>
                  <button
                    type="button"
                    class="py-1.5 px-3 rounded-lg border border-charcoal-600 text-charcoal-300 text-xs font-semibold hover:bg-charcoal-900 transition-colors"
                    @click="relinkOpen = { ...relinkOpen, [survey.surveyId]: false }"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Proposal cards -->
        <template v-else>
          <button
            v-for="proposal in proposals"
            :key="proposal.id"
            type="button"
            class="w-full text-left rounded-2xl border bg-charcoal-800 border-charcoal-600 p-4 hover:shadow-md hover:border-charcoal-600 hover:bg-charcoal-900/50 transition-all cursor-pointer group"
            @click="navigateToProposal(proposal)"
          >
            <!-- Survey + Site -->
            <div class="flex items-start justify-between gap-2 mb-3">
              <div class="min-w-0 flex-1">
                <p class="font-bold text-sm text-charcoal-100 truncate leading-tight">
                  {{ proposal.surveyName }}
                </p>
                <p class="text-xs text-charcoal-400 truncate mt-0.5">
                  <MapPin :size="10" class="inline mr-0.5" :stroke-width="2" />{{
                    proposal.siteName
                  }}
                </p>
              </div>
              <div class="flex items-center gap-2 shrink-0">
                <span class="text-sm font-black" :class="titleColor">
                  {{ formatCurrency(proposal.grandTotal, proposal.currency) }}
                </span>
                <ArrowRight
                  :size="14"
                  class="text-charcoal-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  :stroke-width="2.5"
                />
              </div>
            </div>

            <!-- Lock indicator (hidden for cancelled proposals which are voided, not locked) -->
            <span
              v-if="props.status !== 'cancelled' && isProposalLocked(proposal)"
              data-testid="lock-indicator"
              class="inline-flex items-center gap-1 mb-2 px-1.5 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-500 text-[10px] font-semibold"
            >
              <Lock :size="9" :stroke-width="2.5" />
              Editing locked
            </span>

            <!-- Meta row -->
            <div class="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs mb-3">
              <div>
                <span class="text-charcoal-400 block">Client</span>
                <span class="font-medium text-charcoal-200 truncate block">{{
                  proposal.clientEmail
                }}</span>
              </div>
              <div>
                <span class="text-charcoal-400 block">Sent by</span>
                <span class="font-medium text-charcoal-200 truncate block">{{
                  shortEmail(proposal.createdBy)
                }}</span>
              </div>
              <div>
                <span class="text-charcoal-400 block">Created</span>
                <span class="font-medium text-charcoal-200">{{
                  formatDate(proposal.createdAt)
                }}</span>
              </div>
              <div>
                <span class="text-charcoal-400 block">{{ expiryLabel(proposal) }}</span>
                <span class="font-medium" :class="expiryColor(proposal)">
                  {{ formatDate(proposal.expiresAt) }}
                </span>
              </div>
              <div v-if="proposal.accessedAt">
                <span class="text-charcoal-400 block">Accessed</span>
                <span class="font-medium text-charcoal-200">{{
                  formatDate(proposal.accessedAt)
                }}</span>
              </div>
              <div v-if="proposal.approvedAt">
                <span class="text-charcoal-400 block">Approved</span>
                <span class="font-medium text-emerald-600">{{
                  formatDate(proposal.approvedAt)
                }}</span>
              </div>
              <div v-if="proposal.rejectedAt">
                <span class="text-charcoal-400 block">Rejected</span>
                <span class="font-medium text-red-500">{{ formatDate(proposal.rejectedAt) }}</span>
              </div>
            </div>

            <!-- View link -->
            <a
              :href="`/public/proposals/${proposal.token}`"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors"
              :class="viewLinkClass"
              @click.stop
            >
              <ExternalLink :size="11" :stroke-width="2.5" />
              View proposal
            </a>
          </button>
        </template>
      </div>
    </aside>
  </Transition>
</template>

<script setup lang="ts">
import {
  X,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  ExternalLink,
  MapPin,
  Trash2,
  Link2,
  LayoutTemplate,
  ChevronDown,
  ArrowRight,
  Lock,
} from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { getProposalStatusConfig } from '@/utils/proposal-status-config'
import { useRouter } from 'vue-router'

interface ProposalSummary {
  id: string
  reportId: string
  token: string
  clientEmail: string
  surveyId: string
  surveyName: string
  siteName: string
  grandTotal: number
  currency: string
  createdBy: string
  createdAt: string
  expiresAt: string
  approvedAt: string | null
  accessedAt: string | null
  rejectedAt: string | null
}

interface StartedByVendorReport {
  id: string
  surveyId: string
  surveyName: string
  createdAt: string
  updatedAt: string
}

interface StaleSurvey {
  surveyId: string
  reportCount: number
  reportName: string | null
}

interface NotStartedSurvey {
  surveyId: string
  surveyName: string
}

interface ValidSurvey {
  id: string
  name: string
}

interface Props {
  open: boolean
  status: string | null
  proposals: ProposalSummary[]
  reports: StartedByVendorReport[]
  staleSurveys: StaleSurvey[]
  notStartedSurveys: NotStartedSurvey[]
  validSurveys?: ValidSurvey[]
  loading: boolean
  count: number
  error: string | null
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  count: 0,
  reports: () => [],
  staleSurveys: () => [],
  notStartedSurveys: () => [],
  validSurveys: () => [],
  error: null,
})
const emit = defineEmits<{
  close: []
  deleteStaleAll: []
  deleteStaleSurvey: [surveyId: string]
  relinkStaleSurvey: [payload: { oldSurveyId: string; newSurveyId: string }]
  convertAdhocStale: [surveyId: string]
}>()

type PendingDelete =
  | { type: 'all' }
  | { type: 'survey'; surveyId: string; reportCount: number; reportName: string }

const router = useRouter()

const pendingDelete = ref<PendingDelete | null>(null)
// Track which survey's re-link panel is open (surveyId → selected target survey id)
const relinkOpen = ref<Record<string, boolean>>({})
const relinkSelected = ref<Record<string, string>>({})

// Clear state when drawer closes
watch(
  () => props.open,
  open => {
    if (!open) {
      pendingDelete.value = null
      relinkOpen.value = {}
      relinkSelected.value = {}
    }
  }
)

function confirmDelete() {
  if (!pendingDelete.value) return
  if (pendingDelete.value.type === 'all') {
    emit('deleteStaleAll')
  } else {
    emit('deleteStaleSurvey', pendingDelete.value.surveyId)
  }
  pendingDelete.value = null
}

/** Check if any valid survey name matches the stale report's name (case-insensitive) */
function suggestedSurvey(reportName: string | null): ValidSurvey | null {
  if (!reportName) return null
  const lower = reportName.toLowerCase()
  return effectiveValidSurveys.value.find(s => s.name.toLowerCase() === lower) ?? null
}

function toggleRelink(surveyId: string, reportName: string | null) {
  relinkOpen.value = { ...relinkOpen.value, [surveyId]: !relinkOpen.value[surveyId] }
  if (!relinkSelected.value[surveyId]) {
    const suggestion = suggestedSurvey(reportName)
    relinkSelected.value = {
      ...relinkSelected.value,
      [surveyId]: suggestion?.id ?? effectiveValidSurveys.value[0]?.id ?? '',
    }
  }
}

function doRelink(surveyId: string) {
  const newSurveyId = relinkSelected.value[surveyId]
  if (!newSurveyId) return
  emit('relinkStaleSurvey', { oldSurveyId: surveyId, newSurveyId })
  const updated = { ...relinkOpen.value }
  delete updated[surveyId]
  relinkOpen.value = updated
}

function doConvertAdhoc(surveyId: string) {
  emit('convertAdhocStale', surveyId)
}

function navigateToProposal(proposal: ProposalSummary) {
  emit('close')
  if (proposal.surveyId) {
    void router.push(
      `/surveys/${proposal.surveyId}/appendices?tab=proposal-status&proposalId=${proposal.id}`
    )
  } else {
    void router.push(
      `/proposals/${proposal.reportId}/appendices?tab=proposal-status&proposalId=${proposal.id}`
    )
  }
}

function navigateToReport(report: StartedByVendorReport) {
  emit('close')
  void router.push(`/surveys/${report.surveyId}/appendices`)
}

function navigateToStaleReport(surveyId: string) {
  emit('close')
  void router.push(`/surveys/${surveyId}/appendices`)
}

const effectiveValidSurveys = computed(() => props.validSurveys ?? [])

const displayUnit = computed(() => {
  if (props.status === 'not_started') return props.count === 1 ? 'survey' : 'surveys'
  if (props.status === 'started_by_vendor') return props.count === 1 ? 'report' : 'reports'
  if (props.status === 'stale_reports') return props.count === 1 ? 'stale report' : 'stale reports'
  return props.count === 1 ? 'proposal' : 'proposals'
})

const statusConfig = computed(() => getProposalStatusConfig(props.status ?? ''))

const statusLabel = computed(() => statusConfig.value.label)
const statusDescription = computed(() => statusConfig.value.description)
const statusIcon = computed(() => statusConfig.value.icon)
const iconBg = computed(() => statusConfig.value.colors.iconBg)
const iconColor = computed(() => statusConfig.value.colors.iconColor)
const titleColor = computed(() => statusConfig.value.colors.title)
const headerBg = computed(() => '') // intentionally empty — drawer header is unstyled
const viewLinkClass = computed(() => statusConfig.value.colors.viewLink)

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatCurrency(amount: number, currency = 'AUD'): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

function shortEmail(email: string): string {
  return email.length > 28 ? email.substring(0, 25) + '…' : email
}

function isProposalLocked(p: ProposalSummary): boolean {
  // Locked when approved or rejected (completed), or still active (not yet expired).
  // Intentionally includes proposals the client has not yet accessed (active_not_accessed):
  // once a proposal is sent, editing the underlying report is blocked regardless of
  // whether the client has opened the proposal link.
  if (p.approvedAt !== null || p.rejectedAt !== null) return true
  return new Date(p.expiresAt) > new Date()
}

function expiryLabel(p: ProposalSummary): string {
  if (p.approvedAt) return 'Expired'
  return new Date(p.expiresAt) < new Date() ? 'Expired' : 'Expires'
}

function expiryColor(p: ProposalSummary): string {
  if (p.approvedAt) return 'text-charcoal-400'
  return new Date(p.expiresAt) < new Date() ? 'text-rose-500' : 'text-charcoal-200'
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
